import { useFarmThresholds } from '@/hooks/useFarmThresholds';
import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/apiErrors';
import { qk } from '@/lib/queryKeys';
import { PondNotInFarm, RecentReadingsTable } from '@/components/pond';
import { isPondNotInFarm } from '@/hooks/usePondElsewhere';
import { WaterQualityGrid } from '@/components/pond/WaterQualityGrid';
import { generateAdvice } from '@/lib/advice';
import { toNumberOrNull } from '@/lib/number';
import { cropPhase } from '@/lib/crop';
import type { PondCycleSummary, PondDetailResponse } from '@/types/generated/openapi';
import { CycleStartModal, CycleCloseModal } from '@/components/cycle';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Waves, Play } from 'lucide-react';
import { useCurrentFarm } from '@/hooks/useCurrentFarm';
import { useMembership } from '@/hooks/useMembership';
import { PondPageHeader, HeaderAction } from '@/components/pond/page/PondPageHeader';
import { headerActionClass } from '@/components/pond/page/format';
import { CropTimeline } from '@/components/pond/page/CropTimeline';
import { PondTabs } from '@/components/pond/page/PondTabs';
import { OverviewTab } from '@/components/pond/page/OverviewTab';
import { StockingTab } from '@/components/pond/page/StockingTab';
import { FeedingTab, HarvestsTab, SamplingTab } from '@/components/pond/page/CropLogTabs';
import { ApplicationsTab } from '@/components/pond/page/ApplicationsTab';
import { TECHNICIAN_PREP_NOTE, TECHNICIAN_START_NOTE } from '@/components/pond/page/prepCopy';
import { CancelPrepDialog, MarkStockedDialog, PrestockingTab, StartPrepDialog } from '@/components/pond/page/PrestockingTab';
import { parsePondTab, tabClosedReason, type PondTab } from '@/components/pond/page/tabs';

interface PondSearch {
  tab?: PondTab;
}

export const Route = createFileRoute('/_app/ponds/$pondId')({
  validateSearch: (search: Record<string, unknown>): PondSearch => ({ tab: parsePondTab(search.tab) }),
  component: PondDetailPage,
});

interface PondSiblingsResponse {
  prev: { id: string; name: string } | null;
  next: { id: string; name: string } | null;
}

function PondDetailPage() {
  const { pondId } = Route.useParams();
  const { tab: tabParam } = Route.useSearch();
  const { farmId, isLoading: farmLoading } = useCurrentFarm();
  const { isSupervisorOrAbove } = useMembership();
  const [isStartCycleOpen, setIsStartCycleOpen] = useState(false);
  const [isCloseCycleOpen, setIsCloseCycleOpen] = useState(false);
  const [isStartPrepOpen, setIsStartPrepOpen] = useState(false);
  const [isStockOpen, setIsStockOpen] = useState(false);
  const [isCancelPrepOpen, setIsCancelPrepOpen] = useState(false);
  // Chosen crop is tied to the pond so prev/next navigation falls back to the default.
  const [chosen, setChosen] = useState<{ pondId: string; cycleId: string } | null>(null);
  const chosenCycleId = chosen?.pondId === pondId ? chosen.cycleId : undefined;

  const {
    data: pondData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: chosenCycleId
      ? [...qk.pond.detail(farmId, pondId), chosenCycleId]
      : qk.pond.detail(farmId, pondId),
    // Unwrapped, like the /entry/pond/* routes that share this key: one shape per cache entry.
    queryFn: () =>
      api
        .get<{ data: PondDetailResponse }>(
          `/api/v1/farms/${farmId}/ponds/${pondId}/detail`,
          chosenCycleId ? { params: { cycle_id: chosenCycleId } } : undefined
        )
        .then((r) => r.data),
    enabled: !!farmId,
    // Keep the page up while switching crops, but never show another pond's data.
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[2] === pondId ? prev : undefined),
    // A chosen crop can vanish (deleted mid-view): don't retry the 404, offer the default instead.
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 3,
  });

  const { data: siblingsData } = useQuery({
    queryKey: qk.pond.siblings(farmId, pondId),
    queryFn: () =>
      api.get<{ data: PondSiblingsResponse }>(
        `/api/v1/farms/${farmId}/ponds/${pondId}/siblings`
      ),
    enabled: !!farmId,
  });

  const activeCycleId = pondData?.active_cycle?.id;
  const { data: latestReadings } = useQuery({
    queryKey: ['readings', activeCycleId, 'recent-for-advice'],
    queryFn: () =>
      api.get<{ data: Array<Record<string, unknown>> }>(
        `/api/v1/farms/${farmId}/readings`,
        { params: { cycle_id: activeCycleId ?? '', limit: '1', sort: 'desc' } }
      ),
    enabled: !!farmId && !!activeCycleId,
  });
  const latestReading = latestReadings?.data?.[0];
  const { table } = useFarmThresholds(farmId);
  const advice = latestReading
    ? generateAdvice(
        {
          ph: toNumberOrNull(latestReading.ph as number | string | null),
          dissolved_oxygen: toNumberOrNull(latestReading.dissolved_oxygen as number | string | null),
          temperature_c: toNumberOrNull(latestReading.temperature_c as number | string | null),
          salinity_ppt: toNumberOrNull(latestReading.salinity_ppt as number | string | null),
          ammonia_ppm: toNumberOrNull(latestReading.ammonia_ppm as number | string | null),
          nitrite_ppm: toNumberOrNull(latestReading.nitrite_ppm as number | string | null),
          alkalinity_ppm: toNumberOrNull(latestReading.alkalinity_ppm as number | string | null),
          alkalinity_am: toNumberOrNull(latestReading.alkalinity_am as number | string | null),
          alkalinity_pm: toNumberOrNull(latestReading.alkalinity_pm as number | string | null),
          alkalinity_2: toNumberOrNull(latestReading.alkalinity_2 as number | string | null),
          transparency_cm: toNumberOrNull(latestReading.transparency_cm as number | string | null),
        },
        pondData?.active_cycle?.doc ?? undefined,
        undefined,
        table,
      )
    : [];

  if (!farmId) {
    return farmLoading ? (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <LoadingSpinner size={32} className="text-primary-600" />
        <p className="text-gray-500">Loading...</p>
      </div>
    ) : null;
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <LoadingSpinner size={32} className="text-primary-600" />
        <p className="text-gray-500">Loading pond details...</p>
      </div>
    );
  }

  if (error && chosenCycleId) {
    return (
      <ErrorMessage
        message="That crop is no longer available. Showing the pond's current crop instead."
        onRetry={() => setChosen(null)}
      />
    );
  }

  if (isPondNotInFarm(error)) {
    return <PondNotInFarm pondId={pondId} backTo="/ponds" backLabel="Back to ponds" />;
  }

  if (error || !pondData) {
    return (
      <ErrorMessage
        message="Failed to load pond data. Please try again."
        onRetry={() => refetch()}
      />
    );
  }

  const pond = pondData;
  const siblings = siblingsData?.data;
  const crops = pond.cycles ?? [];
  const crop = pond.selected_cycle ?? null;
  const phase = crop ? cropPhase(crop) : null;
  const hasOpenCrop = crops.some((c) => c.status === 'active' || c.status === 'preparing');
  const requested = tabParam ?? 'overview';
  const tab: PondTab = tabClosedReason(requested, crop) ? 'overview' : requested;

  const cropPicker =
    crop && crops.length > 1 ? (
      <CropSelector crops={crops} value={crop.id} onChange={(id) => setChosen({ pondId, cycleId: id })} />
    ) : null;

  const logApplication = (
    <Link
      to="/ponds/$pondId"
      params={{ pondId: pond.id }}
      search={{ tab: 'applications' }}
      className={headerActionClass(true)}
    >
      + Log application
    </Link>
  );
  const actions =
    crop && phase === 'grow_out' ? (
      <>
        <Link to="/entry/pond/$pondId" params={{ pondId: pond.id }} search={{}} className={headerActionClass()}>
          Enter readings
        </Link>
        {logApplication}
        {isSupervisorOrAbove && <HeaderAction onClick={() => setIsCloseCycleOpen(true)}>Close crop</HeaderAction>}
      </>
    ) : crop && phase === 'prep' ? (
      <>
        {logApplication}
        {isSupervisorOrAbove && (
          <>
            <button type="button" onClick={() => setIsStockOpen(true)} className={headerActionClass(false, 'stock')}>
              Mark as stocked…
            </button>
            <HeaderAction onClick={() => setIsCancelPrepOpen(true)}>Cancel pre-stocking</HeaderAction>
          </>
        )}
      </>
    ) : null;
  const startProps = isSupervisorOrAbove
    ? { onStartCycle: () => setIsStartCycleOpen(true), onStartPrep: () => setIsStartPrepOpen(true) }
    : {};

  return (
    <div className="flex min-w-0 flex-col gap-5">
      <PondPageHeader
        pond={pond}
        crop={crop}
        prev={siblings?.prev}
        next={siblings?.next}
        cropPicker={cropPicker}
        actions={actions}
      />

      {!crop ? (
        <IdlePondState pond={pond} {...startProps} />
      ) : (
        <>
          {!hasOpenCrop && (
            <IdleBanner pondStatus={pond.status} {...startProps} />
          )}

          {phase === 'prep' && !isSupervisorOrAbove && tab !== 'prestocking' && (
            <p className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
              {TECHNICIAN_PREP_NOTE}
            </p>
          )}

          <CropTimeline crop={crop} pondId={pond.id} />
          <PondTabs pondId={pond.id} active={tab} crop={crop} />

          {tab === 'overview' && <OverviewTab farmId={farmId} pond={pond} crop={crop} advice={phase === 'grow_out' ? advice : []} />}
          {tab === 'prestocking' && (
            <PrestockingTab
              farmId={farmId}
              pond={pond}
              crop={crop}
              canSupervise={isSupervisorOrAbove}
              onMarkStocked={() => setIsStockOpen(true)}
            />
          )}
          {tab === 'stocking' && <StockingTab farmId={farmId} pond={pond} crop={crop} canSupervise={isSupervisorOrAbove} />}
          {tab === 'parameters' && (
            <div className="flex min-w-0 flex-col gap-5">
              <WaterQualityGrid
                key={crop.id}
                cycleId={crop.id}
                farmId={farmId}
                defaultRange={phase === 'closed' ? 'cycle' : '7d'}
              />
              <div className="min-w-0">
                <RecentReadingsTable key={crop.id} cycleId={crop.id} farmId={farmId} closed={phase === 'closed'} />
              </div>
            </div>
          )}
          {tab === 'applications' && <ApplicationsTab farmId={farmId} crop={crop} pondAreaHa={pond.area_ha} />}
          {tab === 'feeding' && <FeedingTab farmId={farmId} pondId={pond.id} crop={crop} />}
          {tab === 'sampling' && <SamplingTab farmId={farmId} pondId={pond.id} crop={crop} />}
          {tab === 'harvests' && <HarvestsTab farmId={farmId} pondId={pond.id} crop={crop} />}

          {phase === 'grow_out' && (
            <CycleCloseModal
              open={isCloseCycleOpen}
              onClose={() => setIsCloseCycleOpen(false)}
              farmId={farmId}
              cycleId={crop.id}
              pondName={pond.name}
              cycleNumber={crop.cycle_number ?? null}
              doc={crop.doc ?? 0}
            />
          )}
          {phase === 'prep' && isSupervisorOrAbove && isStockOpen && (
            <MarkStockedDialog open onClose={() => setIsStockOpen(false)} farmId={farmId} pond={pond} crop={crop} />
          )}
          {phase === 'prep' && isSupervisorOrAbove && isCancelPrepOpen && (
            <CancelPrepDialog open onClose={() => setIsCancelPrepOpen(false)} farmId={farmId} pond={pond} crop={crop} />
          )}
        </>
      )}

      {!hasOpenCrop && isSupervisorOrAbove && isStartPrepOpen && (
        <StartPrepDialog
          open
          onClose={() => setIsStartPrepOpen(false)}
          onStarted={() => setChosen(null)}
          farmId={farmId}
          pond={pond}
        />
      )}

      {!hasOpenCrop && isSupervisorOrAbove && (
        <CycleStartModal
          open={isStartCycleOpen}
          onClose={() => setIsStartCycleOpen(false)}
          farmId={farmId}
          pondId={pond.id}
          pondName={pond.name}
        />
      )}
    </div>
  );
}

function cropLabel(c: PondCycleSummary): string {
  const name = `Crop ${c.cycle_number ?? '—'}`;
  const start = c.stocking_date ?? c.prep_start_date;
  if (c.status === 'preparing') {
    return `${name} · preparing${start ? ` since ${format(parseISO(start), 'MMM d, yyyy')}` : ''}`;
  }
  if (!start) return `${name} · ${c.status}`;
  if (!c.stocking_date) return `${name} · ${c.status} in pre-stocking · from ${format(parseISO(start), 'MMM d, yyyy')}`;
  if (c.status === 'active') {
    return `${name} · since ${format(parseISO(start), 'MMM d, yyyy')} (active)`;
  }
  const end = c.actual_harvest_date ? format(parseISO(c.actual_harvest_date), 'MMM d, yyyy') : '…';
  return `${name} · ${format(parseISO(start), 'MMM d')} – ${end}`;
}

interface CropSelectorProps {
  crops: PondCycleSummary[];
  value: string;
  onChange: (cycleId: string) => void;
}

function CropSelector({ crops, value, onChange }: CropSelectorProps) {
  return (
    <label className="flex w-full max-w-sm items-center gap-2">
      <span className="shrink-0 text-xs font-semibold uppercase tracking-wider text-gray-500">View crop</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-h-[40px] w-full min-w-0 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
      >
        {crops.map((c) => (
          <option key={c.id} value={c.id}>
            {cropLabel(c)}
          </option>
        ))}
      </select>
    </label>
  );
}

interface StartActions {
  onStartCycle?: () => void;
  onStartPrep?: () => void;
}

function StartButtons({ onStartCycle, onStartPrep, size }: StartActions & { size: 'sm' | 'lg' }) {
  if (!onStartCycle && !onStartPrep) return null;
  return (
    <div className="flex flex-wrap justify-center gap-2">
      {onStartPrep && (
        <Button variant="primary" size={size} leftIcon={<Play className={size === 'lg' ? 'h-5 w-5' : 'h-4 w-4'} />} onClick={onStartPrep}>
          Start pre-stocking
        </Button>
      )}
      {onStartCycle && (
        <Button variant="secondary" size={size} onClick={onStartCycle}>
          Start a stocked crop
        </Button>
      )}
    </div>
  );
}

interface IdleBannerProps extends StartActions {
  pondStatus: string;
}

/** Idle pond that has history: past crops stay viewable below, read-only. */
function IdleBanner({ pondStatus, onStartCycle, onStartPrep }: IdleBannerProps) {
  const canStart = !!(onStartCycle || onStartPrep);
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3">
        <Waves className="h-5 w-5 shrink-0 text-gray-400" />
        <p className="text-sm text-gray-600">
          Pond is currently <span className="font-medium">{pondStatus}</span>. Showing a past crop
          (read-only).
          {!canStart && ` ${TECHNICIAN_START_NOTE}`}
        </p>
      </div>
      <StartButtons onStartCycle={onStartCycle} onStartPrep={onStartPrep} size="sm" />
    </div>
  );
}

interface IdlePondStateProps extends StartActions {
  pond: PondDetailResponse;
}

function IdlePondState({ pond, onStartCycle, onStartPrep }: IdlePondStateProps) {
  const canStart = !!(onStartCycle || onStartPrep);
  return (
    <Card className="overflow-hidden">
      {/* Decorative wave pattern */}
      <div className="h-32 bg-gradient-to-br from-gray-50 to-gray-100 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='20' viewBox='0 0 100 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M21.184 20c.357-.13.72-.264 1.088-.402l1.768-.661C33.64 15.347 39.647 14 50 14c10.271 0 15.362 1.222 24.629 4.928.955.383 1.869.74 2.75 1.072h6.225c-2.51-.73-5.139-1.691-8.233-2.928C65.888 13.278 60.562 12 50 12c-10.626 0-16.855 1.397-26.66 5.063l-1.767.662c-2.475.923-4.66 1.674-6.724 2.275h6.335zm0-20C13.258 2.892 8.077 4 0 4V2c5.744 0 9.951-.574 14.85-2h6.334zM77.38 0C85.239 2.966 90.502 4 100 4V2c-6.842 0-11.386-.542-16.396-2h-6.225zM0 14c8.44 0 13.718-1.21 22.272-4.402l1.768-.661C33.64 5.347 39.647 4 50 4c10.271 0 15.362 1.222 24.629 4.928C84.112 12.722 89.438 14 100 14v-2c-10.271 0-15.362-1.222-24.629-4.928C65.888 3.278 60.562 2 50 2 39.374 2 33.145 3.397 23.34 7.063l-1.767.662C13.223 10.84 8.163 12 0 12v2z' fill='%23000000' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="p-4 bg-white/80 backdrop-blur-sm rounded-full shadow-sm">
            <Waves className="h-8 w-8 text-gray-400" />
          </div>
        </div>
      </div>

      <Card.Body className="text-center py-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Pond is Currently {pond.status.charAt(0).toUpperCase() + pond.status.slice(1)}
        </h3>
        <p className="text-gray-500 max-w-md mx-auto">
          {canStart
            ? 'No crop in this pond. Start pre-stocking at water-in, or start a crop that is already stocked.'
            : `No crop in this pond. ${TECHNICIAN_START_NOTE}`}
        </p>

        <div className="mt-6">
          <StartButtons onStartCycle={onStartCycle} onStartPrep={onStartPrep} size="lg" />
        </div>

        {pond.notes && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg text-left max-w-md mx-auto">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">
              Notes
            </p>
            <p className="text-sm text-gray-600">{pond.notes}</p>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
